import type { HeadcountReconciliationResult } from '@shared/types'

const MESSAGES = {
  save: {
    high: '✓ Headcount figures look consistent — safe to save.',
    medium: '⚠ Headcount is estimated — verify the figures before saving.',
    low: 'Extracted headcount may be incomplete — please review carefully before saving.',
  },
  proceed: {
    high: '✓ Headcount figures look consistent — safe to proceed.',
    medium: '⚠ Headcount is estimated — verify the figures before proceeding.',
    low: 'Extracted headcount may be incomplete — please review carefully before continuing.',
  },
}

interface Props {
  parsingHeadcount: boolean
  result: HeadcountReconciliationResult | null
  context: 'save' | 'proceed'
  /** More than one roster is queued, so no single headcount exists until one is chosen. */
  multipleRosters?: boolean
  /**
   * True when the file list includes a JSON roster but no XLSX/CSV — there is
   * no client-side JSON FTE parser (see parseStructuredFilesForHeadcount.ts),
   * so `result` is null even though a roster really was added. Without this,
   * the panel showed "Add an XLSX or CSV roster..." for a JSON-only queue,
   * which reads as if the file were ignored — exactly the no-feedback
   * impression PBI 2180 removed elsewhere (code review finding).
   */
  hasUnpreviewableRoster?: boolean
  /** A roster was uploaded but could not be read — "add a roster" would be the wrong thing to say. */
  rosterUnreadable?: boolean
}

export default function HeadcountPanel({ parsingHeadcount, result, context, multipleRosters = false, hasUnpreviewableRoster, rosterUnreadable = false }: Props) {
  const msgs = MESSAGES[context]

  const confidenceColor = !result ? '' :
    result.confidence === 'High' ? 'text-green-700' :
    result.confidence === 'Medium' ? 'text-amber-700' :
    'text-red-700'

  const confidenceBg = !result ? '' :
    result.confidence === 'High' ? 'bg-green-50 border-green-200' :
    result.confidence === 'Medium' ? 'bg-amber-50 border-amber-200' :
    'bg-red-50 border-red-200'

  return (
    <>
      <p className="text-xs font-bold tracking-widest text-gray-500 uppercase mb-2">Headcount Summary</p>
      {parsingHeadcount ? (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-500 animate-pulse">
          Recalculating headcount...
        </div>
      ) : result ? (
        <>
          <p className="text-[11px] text-gray-500 mb-2">
            Calculated from the roster file — figures from PDF, DOCX or PPTX documents are not included.
          </p>
          <div className={`rounded-lg border px-4 py-3 mb-2 ${confidenceBg}`}>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">Extracted headcount</div>
                <div className="text-sm font-bold text-gray-900">{result.extractedCount.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">Source file rows</div>
                <div className="text-sm font-bold text-gray-900">{result.sourceRowCount.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">Confidence</div>
                <div className={`text-sm font-bold ${confidenceColor}`}>{result.confidence}</div>
              </div>
            </div>
          </div>
          {result.confidence === 'High' || result.confidence === 'Medium' ? (
            <p className={`text-xs ${result.confidence === 'High' ? 'text-green-600' : 'text-amber-600'} mb-0.5`}>
              {result.confidence === 'High' ? msgs.high : msgs.medium}
            </p>
          ) : (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-yellow-50 border border-yellow-300 text-xs text-yellow-800">
              <svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              {msgs.low}
            </div>
          )}
        </>
      ) : (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-500">
          <svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {multipleRosters
            ? 'More than one roster file is added. Headcount is calculated for the single roster you choose when you continue.'
            : rosterUnreadable
            ? 'No headcount could be calculated — see the roster notice below.'
            : hasUnpreviewableRoster
            ? "Headcount preview isn't available for JSON rosters. The FTE figures in the file are still sent to Stage 1."
            : 'Add an XLSX or CSV roster to see a headcount summary.'}
        </div>
      )}
    </>
  )
}
